import { useEffect, useRef } from 'react';

// Structured error UI shown when a tokenize attempt failed and we've
// diagnosed the failure as one of three network-class kinds. Replaces
// the string-only `errors._tokenize` rendering for these three kinds,
// so we can attach action-shaped elements (tel/sms anchors, dismiss)
// instead of smuggling React nodes through a string slot.
//
// kind:
//   'device-block'  intuit unreachable, origin+control reachable
//                   (DuckDuckGo, Brave, ad blocker, private DNS)
//   'offline'       origin AND control both unreachable
//                   (customer's connection dropped)
//   'transient'     everything reachable at probe time
//                   (wifi-to-cellular handoff, brief Intuit blip)
//
// Copy design: never blame the customer. All three kinds are framed
// as "our side has a problem" or "your connection dropped," not "your
// browser is broken." The CTA layer is identical across kinds so a
// customer who can't self-heal always has one tap to a human.

const digitsOnly = (s) => String(s || '').replace(/\D/g, '');

const HEAD_COPY = {
  'device-block': "Almost done. Something on your device or network is stopping us from reaching our payment processor.",
  'offline':      "Your device isn't online right now. Reconnect and try again, or:",
  'transient':    "Our payment processor didn't answer just now. That's usually a brief hiccup, not your card, and nothing has been charged. Try again in a few seconds, or:",
};

function browserFixHint(browser) {
  if (!browser) return null;
  switch (browser.id) {
    case 'ddg':
      return "You're using DuckDuckGo. Tap the shield icon near the address bar and turn off Privacy Protection for this page, then try again.";
    case 'brave':
    case 'brave-runtime':
      return "You're using Brave. Tap the lion icon and toggle Shields down for this page, then try again.";
    case 'firefox-ios':
      return "You're using Firefox. Tap the shield icon in the address bar and turn off Enhanced Tracking Protection for this page, then try again.";
    case 'firefox-focus':
      return "You're using Firefox Focus, which blocks payment processors by default. Open the page in Safari or Chrome and try again.";
    default:
      return null;
  }
}

const GENERIC_HINT = "If you use an ad blocker, VPN, or private DNS on this device, try turning it off for this page and retrying.";

export default function PaymentBlockedError({
  kind,
  method,
  browser,
  phone,
  onCallClick,
  onSmsClick,
  onRetryClick,
  onShown,
}) {
  const rootRef = useRef(null);
  const phoneDigits = digitsOnly(phone);
  const phoneDisplay = phone || '';
  const smsBody = "Can't get past payment on my Cabana quote";
  const smsHref = phoneDigits ? `sms:${phoneDigits}?body=${encodeURIComponent(smsBody)}` : null;
  const telHref = phoneDigits ? `tel:${phoneDigits}` : null;

  useEffect(() => {
    // Best-effort re-swap so CallRail's dynamic-number tracking sees
    // the phone we just rendered. .swap() walks the DOM for phone
    // patterns and updates both text and tel: href, so an anchor with
    // visible text is the pattern it supports. No-op if CallRail isn't
    // loaded (often blocked by the same tracker-blocker).
    let cancelled = false;
    let swapTimer = null;
    if (typeof window !== 'undefined' && window.CallRail) {
      swapTimer = setTimeout(() => {
        if (cancelled) return;
        try { window.CallRail.swap(); } catch { /* best-effort */ }
      }, 200);
    }
    // Scroll the alert into view because the submit button often lives
    // below the fold on mobile; without this a customer submitting from
    // the bottom of the page may never see the recovery UI. rAF so the
    // browser has painted the alert before we scroll to it.
    const raf = typeof window !== 'undefined' && window.requestAnimationFrame
      ? window.requestAnimationFrame(() => {
          if (cancelled || !rootRef.current) return;
          try {
            rootRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
          } catch { /* best-effort */ }
        })
      : null;
    // Notify the parent once per mount so telemetry fires per rendered
    // instance, not per catch-path entry.
    if (typeof onShown === 'function') {
      try { onShown(); } catch { /* best-effort */ }
    }
    return () => {
      cancelled = true;
      if (swapTimer) clearTimeout(swapTimer);
      if (raf != null && typeof window !== 'undefined' && window.cancelAnimationFrame) {
        window.cancelAnimationFrame(raf);
      }
    };
    // We intentionally re-fire on kind change (a probe result flipping
    // from device-block to transient is a semantically new alert).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind]);

  const fixHint = kind === 'device-block'
    ? (browserFixHint(browser) || GENERIC_HINT)
    : null;

  return (
    <div
      ref={rootRef}
      className="qp-payment-blocked-error"
      role="alert"
      aria-live="assertive"
      data-block-kind={kind}
    >
      <p className="qp-payment-blocked-headline">{HEAD_COPY[kind] || HEAD_COPY.transient}</p>

      {fixHint && (
        <p className="qp-payment-blocked-fix-hint">{fixHint}</p>
      )}

      <div className="qp-payment-blocked-ctas">
        {telHref && (
          <a
            href={telHref}
            className="qp-payment-blocked-cta qp-payment-blocked-cta-call"
            onClick={onCallClick}
            data-cta="call"
          >
            <span className="qp-payment-blocked-cta-label">Call to finish signup</span>
            <span className="qp-payment-blocked-cta-sub">{phoneDisplay}</span>
          </a>
        )}
        {smsHref && (
          <a
            href={smsHref}
            className="qp-payment-blocked-cta qp-payment-blocked-cta-sms"
            onClick={onSmsClick}
            data-cta="sms"
          >
            <span className="qp-payment-blocked-cta-label">Text us instead</span>
            <span className="qp-payment-blocked-cta-sub">We'll finish it for you</span>
          </a>
        )}
      </div>

      {onRetryClick && (
        <button
          type="button"
          className="qp-payment-blocked-retry"
          onClick={onRetryClick}
          data-cta="retry"
        >
          Dismiss and try the button below again
        </button>
      )}

      {kind !== 'transient' && (
        <p className="qp-payment-blocked-reassure">Nothing has been charged.</p>
      )}
    </div>
  );
}
